import React, { useState } from 'react';
import { Copy, ExternalLink, Users, TrendingUp, DollarSign, Gift, Star, ChevronRight } from 'lucide-react';

interface PromotionStats {
  invitedUsers: number;
  totalSpending: number;
  pendingAmount: number;
  withdrawnAmount: number;
}

interface InvitedUser {
  username: string;
  phone: string;
  registrationTime: string;
  spending: number;
  withdrawableAmount: number;
}

const Promotion: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'pending' | 'reviewing' | 'completed'>('pending');
  const [bankInfo, setBankInfo] = useState({
    realName: '',
    idNumber: '',
    bankName: '',
    accountNumber: ''
  });
  const [copied, setCopied] = useState<string | null>(null);

  const stats: PromotionStats = {
    invitedUsers: 12,
    totalSpending: 25680,
    pendingAmount: 1284,
    withdrawnAmount: 5420
  };

  const invitedUsers: InvitedUser[] = [
    {
      username: 'user001',
      phone: '138****5678',
      registrationTime: '2024-06-20',
      spending: 2500,
      withdrawableAmount: 125
    },
    {
      username: 'user002',
      phone: '139****1234',
      registrationTime: '2024-06-18',
      spending: 1800,
      withdrawableAmount: 90
    },
    {
      username: 'user003',
      phone: '136****9876',
      registrationTime: '2024-06-15',
      spending: 1200,
      withdrawableAmount: 60
    }
  ];

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform">
                <Gift className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <Star className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent mb-4">
            推广中心
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            邀请好友使用平台，共享收益回报
          </p>
          <div className="text-sm text-gray-500 mt-2">
            更新时间：{new Date().toLocaleString()}
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">已邀请人数</p>
                <p className="text-3xl font-bold text-pink-600 group-hover:scale-110 transition-transform">
                  {stats.invitedUsers}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">用户累计消费（元）</p>
                <p className="text-3xl font-bold text-blue-600 group-hover:scale-110 transition-transform">
                  {stats.totalSpending.toLocaleString()}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">待提现金额（元）</p>
                <p className="text-3xl font-bold text-orange-600 group-hover:scale-110 transition-transform">
                  {stats.pendingAmount.toLocaleString()}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">累计获得（元）</p>
                <p className="text-3xl font-bold text-emerald-600 group-hover:scale-110 transition-transform">
                  {stats.withdrawnAmount.toLocaleString()}
                </p>
              </div>
              <div className="w-14 h-14 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Gift className="w-7 h-7 text-white" />
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-8">
            {/* Promotion Info */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <Copy className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">推广信息</h3>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 border border-pink-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-700 font-medium">我的邀请链接</span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => copyToClipboard("https://www.bkunyun.com/v2/page...", "link")}
                        className="p-2 bg-pink-100 text-pink-600 rounded-xl hover:bg-pink-200 hover:scale-110 transition-all"
                        title="复制链接"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a 
                        href="https://www.bkunyun.com/v2/page..." 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 bg-purple-100 text-purple-600 rounded-xl hover:bg-purple-200 hover:scale-110 transition-all"
                        title="打开链接"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm px-4 py-3 rounded-xl border border-pink-200 text-sm font-mono text-gray-700 break-all">
                    https://www.bkunyun.com/v2/page...
                  </div>
                  {copied === 'link' && (
                    <div className="text-sm text-pink-600 mt-2 flex items-center space-x-1">
                      <Copy className="w-3 h-3" />
                      <span>已复制到剪贴板</span>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-700 font-medium">我的邀请码</span>
                    <button
                      onClick={() => copyToClipboard("rc-H56sSCKoKT", "code")}
                      className="p-2 bg-blue-100 text-blue-600 rounded-xl hover:bg-blue-200 hover:scale-110 transition-all"
                      title="复制邀请码"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="bg-white/80 backdrop-blur-sm px-4 py-3 rounded-xl border border-blue-200 text-lg font-mono text-gray-700 text-center">
                    rc-H56sSCKoKT
                  </div>
                  {copied === 'code' && (
                    <div className="text-sm text-blue-600 mt-2 flex items-center space-x-1">
                      <Copy className="w-3 h-3" />
                      <span>已复制到剪贴板</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* User List */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex space-x-1 bg-gray-100 rounded-xl p-1">
                  {(['pending', 'reviewing', 'completed'] as const).map((tab) => (
                    <button
                      key={tab}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === tab 
                          ? 'bg-white text-gray-900 shadow-sm' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab === 'pending' ? '待返现' : tab === 'reviewing' ? '审核中' : '已返现'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full table-fixed min-w-[800px] lg:min-w-[1200px]">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                    <tr>
                      <th className="w-[60px] lg:w-[80px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        <input type="checkbox" className="rounded border-gray-300" />
                      </th>
                      <th className="w-[100px] lg:w-[140px] px-3 lg:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">用户名</th>
                      <th className="w-[120px] lg:w-[160px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">手机号</th>
                      <th className="w-[140px] lg:w-[180px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">注册时间</th>
                      <th className="w-[100px] lg:w-[140px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">消费金额</th>
                      <th className="w-[100px] lg:w-[140px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">可提现金额</th>
                      <th className="w-[80px] lg:w-[160px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {invitedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center space-y-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                              <Users className="w-8 h-8 text-gray-400" />
                            </div>
                            <div className="text-gray-500">暂无邀请用户数据</div>
                            <div className="text-sm text-gray-400">开始邀请好友来获得奖励吧！</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      invitedUsers.map((user, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-3 lg:px-6 py-4">
                            <input type="checkbox" className="rounded border-gray-300" />
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900 text-sm lg:text-base">{user.username}</div>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-xs lg:text-sm text-gray-500">{user.phone}</td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap text-xs lg:text-sm text-gray-500">{user.registrationTime}</td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                            <span className="font-medium text-gray-900 text-sm lg:text-base">¥{user.spending}</span>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                            <span className="font-medium text-emerald-600 text-sm lg:text-base">¥{user.withdrawableAmount}</span>
                          </td>
                          <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                            <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1 text-sm lg:text-base">
                              <span>查看</span>
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-700">每页行数：</span>
                  <select className="border border-gray-300 rounded-lg px-3 py-1 text-sm">
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-700">0-0 of 0</span>
                  <div className="flex space-x-1">
                    <button className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                      首页
                    </button>
                    <button className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                      上一页
                    </button>
                    <button className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                      下一页
                    </button>
                    <button className="px-3 py-1 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                      末页
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-8">
            {/* Bank Info */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">收款信息</h3>
                  </div>
                  <button className="text-blue-600 hover:text-blue-700 font-medium">设置</button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {[
                  { label: '真实姓名', value: bankInfo.realName },
                  { label: '身份证号', value: bankInfo.idNumber },
                  { label: '开户银行', value: bankInfo.bankName },
                  { label: '银行账户', value: bankInfo.accountNumber }
                ].map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-sm text-gray-600">{item.label}</span>
                    <span className="text-sm text-gray-900">{item.value || '未设置'}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                      <Star className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">常见问题</h3>
                  </div>
                  <button className="text-blue-600 hover:text-blue-700 font-medium">更多</button>
                </div>
              </div>
              
              <div className="p-6 space-y-3">
                {[
                  '为什么提现金额没有到账？',
                  '提现多久才能到账？',
                  '如何提高推广转化率？',
                  '推广奖励如何计算？'
                ].map((question, index) => (
                  <button key={index} className="w-full text-left p-3 rounded-xl hover:bg-gray-50 transition-colors group">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 group-hover:text-gray-900">{question}</span>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Promotion Tips */}
            <div className="bg-gradient-to-r from-pink-500 to-purple-600 rounded-2xl p-6 text-white">
              <div className="flex items-center space-x-3 mb-4">
                <Gift className="w-8 h-8" />
                <h3 className="text-lg font-bold">推广技巧</h3>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start space-x-2">
                  <Star className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>分享到社交媒体获得更多曝光</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Star className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>为好友详细介绍平台优势</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Star className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>持续跟进用户使用情况</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Promotion;
